import { Box, Divider } from "@material-ui/core";
import styles from "./BetaForm.module.css";
import { Field, Formik } from "formik";
import {
  AppButton,
  AppButtonStyles,
  Spinner,
  Alert,
  AlertIcon,
} from "solveiq.designsystem";
import { object, string } from "yup";
import TextField from "./TextField";
import PhoneField from "./PhoneField";
import RESTGatewayAPI from "../../api/gatewayAPI";
import { useState } from "react";
import ThanksModal from "./ThanksModal";
import { useSearchParams } from "react-router-dom";

const validationSchema = object({
  firstName: string().required("First Name is required"),
  lastName: string().required("Last Name is required"),
  phoneNumber: string().required("Phone Number is required"),
  companyName: string().required("Company Name is required"),
  email: string().required("Email is required").email("This is not an email"),
  confirmEmail: string()
    .required("Confirm Email is required")
    .email("This is not an email"),
});

const initialState = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  companyName: "",
  email: "",
  confirmEmail: "",
};

export interface IBetaForm {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  companyName: string;
  email: string;
  confirmEmail: string;
}

declare const agentUiURL: string;

// Function to validate form data
const validate = (values: IBetaForm) => {
  const errors: {
    email?: string;
    confirmEmail?: string;
    phoneNumber?: string;
  } = {};

  // Validate Email
  if (values?.email !== values?.confirmEmail) {
    errors.email = "The email and the confirmation email are different";
    errors.confirmEmail = "The email and the confirmation email are different";
  }

  // Valid phone number mask 123-456-7890
  if (values?.phoneNumber.length !== 12) {
    errors.phoneNumber = "Invalid phone number";
  }

  return errors;
};

const BetaForm: React.FC = () => {

  const [searchParams] = useSearchParams();
  const uuid = searchParams.get("UUID");

  // The user already exist, feedback modal
  const [showExistModal, setShowExistModal] = useState(false);
  // Something went wrong in the service
  const [showErrorModal, setShowErrorModal] = useState(false);
  // Display Thanks for Joining modal
  const [showThanksModal, setShowThanksModal] = useState(false);
  // redirect URL in case of success
  const [redirectUrl, setRedirectUrl] = useState("");

  /* method to submit new team members*/
  const handleSubmit = async (values: IBetaForm) => {
    // Verify if the user exists
    const getExistAccountUrl = `/api/Checkout/existingaccount/${values?.email}`;

    const responseExistAccount = await RESTGatewayAPI.get(getExistAccountUrl);
    // Should contain a property named AllowPurchase
    const shouldAllowCreateAccount =
      responseExistAccount?.data?.data?.AllowPurchase;

    //In case false the account already exists
    if (shouldAllowCreateAccount === false) {
      setShowExistModal(true);
      return;
    }

    //In case undefined something went wrong in the backend
    if (shouldAllowCreateAccount === undefined) {
      setShowErrorModal(true);
      return;
    }

    const createUsersUrl = `/api/Checkout/betasubscribe`;

    const body = {
      ProductID: 5,
      PaymentInfo: {
        Name: `${values?.firstName} ${values?.lastName}`,
        Email: values?.email,
        PhoneNumber: values?.phoneNumber?.replaceAll("-", ""),
        BusinessName: values?.companyName,
      },
      SeatCount: 20,
      QueryStringData: new URLSearchParams(window.location.search).toString(),
    };

    const submitResponse = await RESTGatewayAPI.post(createUsersUrl, body);

    if (submitResponse?.status === 200 && submitResponse?.data?.succeeded) {
      const builtUrl = `${submitResponse?.data?.data?.RedirectUrl}`;
      setRedirectUrl(builtUrl);
      setShowThanksModal(true);
    } else {
      setShowErrorModal(true);
    }
  };

  return (
    <Box data-qatag="betaFormContainer" className={styles.betaFormContainer}>
      <Box data-qatag="betaFormContent" className={styles.betaFormContent}>
        <Box data-qatag="titleTextBox">
          <Box data-qatag="titleText" className={styles.betaFormTitle}>
            Quick & Simple Registration
          </Box>
          <Box
            data-qatag="betaFormSubtitle"
            className={styles.betaFormSubtitle}
          >
            No credit card required for 30 days unlimited access*.
          </Box>
          <Formik
            data-qatag="formik"
            initialValues={initialState}
            validate={validate}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {(props) => (
              <form data-qatag="form" onSubmit={props.handleSubmit}>
                <Box data-qatag="container" className={styles.betaForm}>
                  <Box
                    data-qatag="firstNameContainer"
                    width="100%"
                    className={styles.fieldContainer}
                  >
                    <Field
                      data-qatag="field"
                      component={TextField}
                      name="firstName"
                      type="text"
                      label="First Name"
                      className={styles.field}
                    />
                  </Box>

                  <Box
                    data-qatag="lastNameContainer"
                    width="100%"
                    className={styles.fieldContainer}
                  >
                    <Field
                      data-qatag="field"
                      component={TextField}
                      name="lastName"
                      type="text"
                      label="Last Name"
                      className={styles.field}
                    />
                  </Box>

                  <Box
                    data-qatag="phoneNumberContainer"
                    width="100%"
                    className={styles.fieldContainer}
                  >
                    <Field
                      data-qatag="field"
                      component={TextField}
                      name="phoneNumber"
                      type="text"
                      label="Phone Number"
                      className={styles.field}
                      InputProps={{
                        inputComponent: PhoneField,
                      }}
                    />
                  </Box>

                  <Box
                    data-qatag="companyNameContainer"
                    width="100%"
                    className={styles.fieldContainer}
                  >
                    <Field
                      data-qatag="field"
                      component={TextField}
                      name="companyName"
                      type="text"
                      label="Company Name"
                      className={styles.field}
                    />
                  </Box>

                  <Box
                    data-qatag="emailAddressContainer"
                    width="100%"
                    className={styles.fieldContainer}
                  >
                    <Field
                      data-qatag="field"
                      component={TextField}
                      name="email"
                      type="text"
                      label="Email Address"
                      className={styles.field}
                    />
                  </Box>

                  <Box
                    data-qatag="confirmEmailAddressContainer"
                    width="100%"
                    className={styles.fieldContainer}
                  >
                    <Field
                      data-qatag="field"
                      component={TextField}
                      name="confirmEmail"
                      type="text"
                      label="Confirm Email Address"
                      className={styles.field}
                    />
                  </Box>

                  {/* Submit button send invites */}
                  <Box
                    data-qatag="buttonContainer"
                    className={styles.buttonContainer}
                  >
                    <AppButton
                      data-qatag="customButton"
                      buttonStyle={AppButtonStyles.Green}
                      className={styles.customButton}
                      type="submit"
                    >
                      Create Solve iQ Account
                    </AppButton>
                  </Box>
                </Box>
                {/* Loading Spinner */}
                <Spinner
                  isActive={props.isSubmitting}
                  text="Creating Your Account..."
                />
              </form>
            )}
          </Formik>
        </Box>
        {/* Divider */}
        <Box data-qatag="divider" className={styles.divider}>
          <Divider data-qatag="dividerItem" />
        </Box>
        {/* Alert Account Already exist */}
        <Alert
          isOpen={showExistModal}
          icon={AlertIcon.Warning}
          approveHandler={() => {
            setShowExistModal(false);
            window.open(agentUiURL, "_blank", "noopener,noreferrer");
          }}
          closeHandler={() => {
            setShowExistModal(false);
          }}
          title="Existing Account Detected"
          text="We've detected that you already have an account with Solve iQ Business."
          buttonText="Manage Account"
        />
        {/* Alert Something went wrong */}
        <Alert
          isOpen={showErrorModal}
          icon={AlertIcon.Warning}
          approveHandler={() => {
            setShowErrorModal(false);
          }}
          closeHandler={() => {
            setShowExistModal(false);
          }}
          title="Something went wrong"
          text="Please retry later."
          buttonText="Close"
        />
        {/* Thanks for Joining modal */}
        <ThanksModal
          handleOnClose={() => setShowThanksModal(false)}
          isOpen={showThanksModal}
          urlRedirect={redirectUrl}
        />
      </Box>
    </Box>
  );
};

export default BetaForm;
